import { useEffect, useRef } from "react"
import type { MutableRefObject } from "react"
import { useAnnotationsStore } from "store/annotations.store"
import AnnotationService from "services/annotations.service"
import { useFurnitureLabels } from "hooks/furnitureLabels.hook"
import { useViewerStore } from "store/viewer.store"
import { useQuotationPeerSyncStore } from "store/quotationPeerSync.store"
import type { TPageElevationData, TPageInfo } from "types/elevationReferences.type"
import type { TAnnotationCategory } from "types/annotations.type"

// A single selected entity — the only shape the peer-driven resync below can restore (see there).
export type TSelectedAnnotationEntity = { type: "eref" | "furniture" | "ecall"; id: number }

export type TFreshAnnotationsData = { relatedObjects: TPageElevationData[]; pageInfos: TPageInfo[] } | undefined

export const useAnnotations = (
	relatedObjects: TPageElevationData[] | undefined,
	pageInfos?: TPageInfo[] | undefined,
	// Current selection (single-entity only), kept live via a ref rather than a value so it's never
	// a dep of the resync effect — a plain click shouldn't re-run it. #1430 follow-up.
	selectedEntityRef?: MutableRefObject<TSelectedAnnotationEntity | null>,
	// Read the freshest related_objects/page_infos straight from the query cache for the peer-driven
	// redraw below, bypassing this hook's own `relatedObjects`/`pageInfos` props. Optional — omitted
	// call sites (and every pre-existing test) keep using the props as before.
	//
	// Why this exists (#1430 review — "the other window has strange colors" after a peer edit): the
	// peer-sync epoch (a zustand bump) and this hook's `relatedObjects` prop (a TanStack Query
	// observer notification) are two INDEPENDENTLY scheduled updates. Nothing guarantees they commit
	// in the same React render. If the epoch's render commits first, this effect redraws against
	// stale props and marks that epoch "applied" — so when `relatedObjects` finally does update on a
	// later render, the epoch guard below sees no new epoch and skips it, leaving the canvas
	// permanently drawn from the pre-update data. Reading the cache directly at the moment the effect
	// actually runs sidesteps prop-propagation timing entirely: `invalidateFromPeer`
	// (quotationWindowSync.helper.ts) only bumps the epoch AFTER its own refetch has already written
	// the fresh response into this exact cache entry, so the cache is always current by then.
	getFreshAnnotationsData?: () => TFreshAnnotationsData,
	// Wraps the restore-selection call below so it re-enters the click pipeline SILENTLY — see that
	// call site's own comment for why this is required, not optional polish.
	selectSilently?: (select: () => void) => void,
) => {
	const instance = useViewerStore((state) => state.instance)
	const { visibility, toggleCategoryVisibility, setUnconnectedVisible, setSelectedAnnotation } = useAnnotationsStore()
	const hasRenderedRef = useRef(false)
	// Peer-sync signal (see quotationPeerSync.store). The resync effect below depends on
	// `peerUpdateEpoch` itself, not just `relatedObjects` — the bump lands on its own later render,
	// so an effect keyed only on `relatedObjects` would never re-run for it.
	const peerUpdateEpoch = useQuotationPeerSyncStore((state) => state.peerUpdateEpoch)
	const appliedPeerEpochRef = useRef(peerUpdateEpoch)

	// Set up annotation selection callback
	useEffect(() => {
		AnnotationService.onAnnotationSelected = (metadata) => {
			setSelectedAnnotation(metadata)
		}

		return () => {
			AnnotationService.onAnnotationSelected = undefined
		}
	}, [setSelectedAnnotation])

	// Render annotations when relatedObjects (or page-name boxes) are available and the document
	// is loaded. Pages may exist even where a page has no related_objects entry, so either source
	// is enough to render.
	useEffect(() => {
		if (!instance || (!relatedObjects?.length && !pageInfos?.length)) return

		const docViewer = instance.Core.documentViewer

		// Also listen for document load in case it hasn't loaded yet
		const onDocumentLoaded = () => {
			AnnotationService.renderAnnotations(relatedObjects ?? [], pageInfos ?? [])
			// REQ-395/396: notes are a separate pass (own permission model, no metadataMap/category
			// tracking) — see AnnotationService.renderNotes' own comment for why it stays independent
			// of renderAnnotations, the same way highlights and furniture labels do.
			AnnotationService.renderNotes(relatedObjects ?? [])
			hasRenderedRef.current = true
		}

		docViewer.addEventListener("documentLoaded", onDocumentLoaded)

		return () => {
			docViewer.removeEventListener("documentLoaded", onDocumentLoaded)
		}
	}, [instance, relatedObjects, pageInfos])

	// Connect / move / detach splice the cache without a re-render, so keep each furniture's eref link
	// current here — navigation framing (REQ-455) groups objects by it.
	useEffect(() => {
		if (relatedObjects) AnnotationService.syncFurnitureErefLinks(relatedObjects)
	}, [relatedObjects])

	// Redraw the layer when a PEER window's edit lands — a local edit already draws its own
	// annotation directly, a peer's edit has no such hook here. Gated on the epoch, not on every
	// `relatedObjects` change, since this is a full (costly) rebuild. Safe against the freehand
	// loop: `renderAnnotations` marks every highlight "own" before its `addAnnotations` call.
	useEffect(() => {
		if (!instance || (!relatedObjects?.length && !pageInfos?.length)) return
		// Initial paint (documentLoaded) hasn't happened yet — nothing to resync from.
		if (!hasRenderedRef.current) return
		// No peer update since the last redraw.
		if (appliedPeerEpochRef.current === peerUpdateEpoch) return
		appliedPeerEpochRef.current = peerUpdateEpoch

		// See getFreshAnnotationsData's own comment above for why this reads the cache instead of
		// trusting the closure's relatedObjects/pageInfos.
		const fresh = getFreshAnnotationsData?.()
		const freshRelatedObjects = fresh?.relatedObjects ?? relatedObjects ?? []
		const freshPageInfos = fresh?.pageInfos ?? pageInfos ?? []

		AnnotationService.renderAnnotations(freshRelatedObjects, freshPageInfos)
		AnnotationService.renderNotes(freshRelatedObjects)

		// The rebuild wipes the on-canvas selection outline; restore it the way a normal click would.
		// Single-entity only — a batch/connect-objects/drag session isn't torn down here, so one in
		// progress when a peer update lands can end up pointing at stale annotations.
		//
		// MUST run through `selectSilently` (#1430 review — "marking a cabinet reviewed selects an
		// eref in the parent screen"): `AnnotationService.select*` fires Apryse's `annotationSelected`
		// event, which re-enters the SAME click-handler pipeline a real click goes through — that is
		// documented behavior (see `selectEref`'s own comment: "the existing annotationSelected
		// listeners then open the reference sidebar as usual"). Calling it bare therefore doesn't just
		// restore the outline, it re-fires `notifySmartNavSelection` for whatever was ALREADY
		// selected — which now also broadcasts a `peer-selection` message, turning "an unrelated
		// mutation landed" into "this window just selected X", and handing the OTHER window a
		// selection change nobody asked for.
		const selected = selectedEntityRef?.current
		const restoreSelection = () => {
			if (selected?.type === "eref") AnnotationService.selectEref(selected.id)
			else if (selected?.type === "furniture") AnnotationService.selectFurniture(selected.id)
			else if (selected?.type === "ecall") AnnotationService.selectEcall(selected.id)
		}
		if (selected) {
			if (selectSilently) selectSilently(restoreSelection)
			else restoreSelection()
		}
	}, [peerUpdateEpoch, instance, relatedObjects, pageInfos, selectedEntityRef, getFreshAnnotationsData, selectSilently])

	// On-drawing furniture info labels (REQ-401). Order-independent of the render effect above:
	// `renderAnnotations` clears only the entity highlights, never the labels, so it does not
	// matter which of the two documentLoaded listeners fires first.
	useFurnitureLabels(relatedObjects)

	// Sync visibility changes to service
	useEffect(() => {
		if (!hasRenderedRef.current) return

		const categories = Object.keys(visibility) as TAnnotationCategory[]
		for (const category of categories) {
			AnnotationService.setCategoryVisibility(category, visibility[category])
		}
	}, [visibility])

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			AnnotationService.cleanup()
			hasRenderedRef.current = false
		}
	}, [])

	return { visibility, toggleCategoryVisibility, setUnconnectedVisible }
}
